import { currentTime, each, inArray } from './Helpers';

/**
 * Manages cookies for the app.
 *
 * @since 5.0.0
 */
export default class Cookie {
	static DAY_SECONDS = 24 * 60 * 60;

	static DELETE_EXP = 0;
	static SESSION_EXP = -1;
	static FAUX_SESSION_EXP = 20 * 60;

	static PERSISTENT = '_omappvp';
	static SESSION = '_omappvs';
	static SUCCESS = 'omSuccessCookie';
	static GLOBAL_SUCCESS = 'omGlobalSuccessCookie';
	static GLOBAL_INTERACTION = 'omGlobalInteractionCookie';

	static LAST_CACHED = null;
	static COOKIE_CACHE = null;

	/**
	 * Get a cookie by name or pattern.
	 *
	 * @since 5.0.0
	 *
	 * @param   {string|RegExp} name  The name of the cookie to retrieve or a pattern.
	 * @param   {boolean}  all  Whether it should return the first value or all the found values
	 *
	 * @returns {string|Array|null} Returns the value of a cookie or null if the cookie doesn't exist.
	 */
	static get = (name, all = false) => {
		let cookies = Cookie.getCache();
		let result = [];

		const test = name instanceof RegExp ? (key) => name.test(key) : (key) => key === name;

		for (const key in cookies) {
			if (test(key)) {
				let value = cookies[key];

				if (Array.isArray(value)) {
					for (let i = 0; i < value.length; i++) {
						result.push(value[i]);
					}
				} else {
					result.push(value);
				}
			}
		}

		if (0 < result.length && all) {
			return result;
		}

		return 0 < result.length ? result[0] : null;
	};

	/**
	 * Retrieve all cookies that are currently set.
	 *
	 * @since 5.0.0
	 *
	 * @returns {Object} Returns an object of all cookies that are currently set.
	 */
	static all = () => {
		let parsed = {};
		if (document.cookie && '' !== document.cookie) {
			let cookies = document.cookie.split(';');
			cookies.map((cookie, key) => {
				let data = cookies[key].split(/=(.*)/);
				try {
					data[0] = decodeURIComponent(data[0].replace(/^ /, ''));
				} catch (e) {}
				try {
					data[1] = decodeURIComponent(data[1]);
				} catch (e) {}

				if (parsed[data[0]]) {
					if (!(parsed[data[0]] instanceof Array)) {
						const prev = parsed[data[0]];
						parsed[data[0]] = [];
						parsed[data[0]].push(prev);
					}

					parsed[data[0]].push(data[1]);
					return;
				}

				parsed[data[0]] = data[1];
			});
		}

		Cookie.setCache(parsed);

		return parsed;
	};

	/**
	 * Create a cookie.
	 *
	 * @since 5.0.0
	 *
	 * @param   {string}  name  The name of the cookie to create.
	 * @param   {string}  value The value of the cookie.
	 * @param   {number}  secs  The length of time this cookie will last in seconds.
	 * @param   {boolean} cross Whether or not this cookie needs to work across domains.
	 *
	 * @returns {void}
	 */
	static create = (name, value, secs, cross) => {
		// If we have a days value, set it in the expiry of the cookie.
		let expires = '';
		if (secs || Cookie.DELETE_EXP === secs) {
			// If -1 or 0 is our value, set a session based cookie instead of a persistent cookie.
			if (Cookie.SESSION_EXP === secs || Cookie.DELETE_EXP === secs) {
				expires = '';
			} else {
				let date = new Date();
				date.setTime(date.getTime() + secs * 1000);
				expires = '; expires=' + date.toUTCString();
			}
		} else {
			expires = '; expires=Thu, 01 Jan 1970 00:00:01 GMT';
		}

		let cookie = name + '=' + value + expires + '; path=/';
		if (cross) {
			cookie += ';domain=.' + Cookie.domain();
		}

		// If https, set secure.
		const protocol = window.location.protocol;

		if (protocol && 'https:' === protocol) {
			cookie += ';secure';
		}

		// Set SameSite.
		cookie += cross && protocol && 'https:' === protocol ? ';SameSite=None' : ';SameSite=Lax';

		// Write the cookie.
		document.cookie = cookie;

		Cookie.clearCache();
	};

	/**
	 * Create a (faux) session cookie.
	 *
	 * @since  5.22.40
	 *
	 * @param   {string}  name  The name of the cookie to create.
	 * @param   {string}  value The value of the cookie.
	 * @param   {boolean} cross Whether or not this cookie needs to work across domains.
	 *
	 * @returns {void}
	 */
	static sessionCreate = (name, value, cross) => {
		return Cookie.create(name, value, Cookie.FAUX_SESSION_EXP, cross);
	};

	/**
	 * Delete a cookie by name.
	 *
	 * @since 5.0.0
	 *
	 * @param   {string} name The name of the cookie being deleted.
	 *
	 * @returns {void}
	 */
	static delete = (name) => {
		Cookie.create(name);
		Cookie.create(name, '', false, true);

		Cookie.clearCache();
	};

	/**
	 * Get the base domain name for a cookie.
	 *
	 * @since 5.0.0
	 *
	 * @returns {string} The current domain in a way that cookies like.
	 */
	static domain = () => {
		let tempCookie = 'temporary_get_base_domain=cookie';
		let hostname = document.location.hostname.split('.');
		let baseDomain;

		// Loop for each segment of the hostname (starting from the right)
		for (let i = hostname.length - 1; 0 <= i; i--) {
			baseDomain = hostname.slice(i).join('.');
			document.cookie = tempCookie + ';domain=.' + baseDomain + ';';

			// If the temp cookie was created successfully then the base domain is good
			if (-1 < document.cookie.indexOf(tempCookie)) {
				document.cookie =
					tempCookie.split('=')[0] + '=;domain=.' + baseDomain + ';expires=Thu, 01 Jan 1970 00:00:01 GMT;';
				return baseDomain;
			}
		}
	};

	/**
	 * Checks whether or not cookies are enabled.
	 *
	 * @since 5.0.0
	 *
	 * @returns {boolean} Are cookies enabled or not?
	 */
	static enabled = () => {
		let enabled = navigator.cookieEnabled ? true : false;
		if ('undefined' === typeof navigator.cookieEnabled && !enabled) {
			document.cookie = 'testcookie';
			enabled = -1 !== document.cookie.indexOf('testcookie');
		}
		return enabled;
	};

	/**
	 * Deletes all visitor/OM cookies.
	 *
	 * @since 5.22.40
	 *
	 * @returns {void}
	 */
	static deleteOmCookies = () => {
		// Remove the visitor cookies.
		Cookie.delete(Cookie.PERSISTENT);
		Cookie.delete(Cookie.SESSION);

		// Loop through all cookies, and remove any with the `om` prefix.
		each(Cookie.all(), (c) => {
			if (0 === c.indexOf('om')) {
				Cookie.delete(c);
			}
		});
	};

	/**
	 * Get the OM persistent cookie (_omappvp) cookie value.
	 *
	 * @since 5.22.40
	 *
	 * @returns {mixed} The _omappvp cookie value.
	 */
	static getPersistent = () => {
		return Cookie.get(Cookie.PERSISTENT);
	};

	/**
	 * Set the OM persistent cookie (_omappvp).
	 *
	 * @since   5.22.40
	 *
	 * @param   {string} value The value of the cookie.
	 * @param   {boolean} cross Whether or not this cookie needs to work across domains.
	 *
	 * @returns {void}
	 */
	static setPersistent = (value, cross) => {
		const prettyMuchNever = 3999 * Cookie.DAY_SECONDS;

		Cookie.create(Cookie.PERSISTENT, value, prettyMuchNever, cross);
	};

	/**
	 * Get the OM session cookie (_omappvs) cookie value.
	 *
	 * @since 5.22.40
	 *
	 * @returns {mixed} The _omappvs cookie value.
	 */
	static getSession = () => {
		return Cookie.get(Cookie.SESSION);
	};

	/**
	 * Set the OM session cookie (_omappvs).
	 *
	 * @since  5.22.40
	 *
	 * @param   {boolean} cross Whether or not this cookie needs to work across domains.
	 *
	 * @returns {void}
	 */
	static setSession = (cross) => {
		Cookie.sessionCreate(Cookie.SESSION, currentTime(), cross);
	};

	/**
	 * Get expiration value in seconds.
	 *
	 * @since  5.22.40
	 *
	 * @param  {int} expirationDays Number of days, or 0 for session cookie, or -1 for expired cookie.
	 * @param  {bool} convertSession Whether to convert a session cookie exp to a faux session exp.
	 *
	 * @returns {int}                The expiration in seconds.
	 */
	static getExpiration = (expirationDays, convertSession = true) => {
		let exp = inArray(expirationDays, [Cookie.DELETE_EXP, Cookie.SESSION_EXP])
			? expirationDays
			: expirationDays * Cookie.DAY_SECONDS;

		if (convertSession && Cookie.SESSION_EXP === exp) {
			exp = Cookie.FAUX_SESSION_EXP;
		}

		return exp;
	};

	/**
	 * Get cached cookie object.
	 *
	 * @since 5.23.58
	 *
	 * @returns {Object} The cached cookie object or null.
	 */
	static getCache() {
		if (Cookie.COOKIE_CACHE && 1550 > Date.now() - Cookie.LAST_CACHED) {
			return Cookie.COOKIE_CACHE;
		}

		return Cookie.all();
	}

	/**
	 * Set cached cookie object.
	 *
	 * @since 5.23.58
	 *
	 * @param {Object} cache The cached cookie object.
	 *
	 * @returns {void}
	 */
	static setCache(cache) {
		Cookie.COOKIE_CACHE = cache;
		Cookie.LAST_CACHED = Date.now();
	}

	/**
	 * Clear cached cookie object.
	 *
	 * @since 5.23.58
	 *
	 * @returns {void}
	 */
	static clearCache() {
		Cookie.COOKIE_CACHE = null;
		Cookie.LAST_CACHED = null;
	}
}
